import React, { useState, useEffect } from 'react';
import { BiRefresh, BiX } from 'react-icons/bi';

const UpdateNotification = () => {
  const [showUpdate, setShowUpdate] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [currentTask, setCurrentTask] = useState('');
  const [estimatedTime, setEstimatedTime] = useState(null);

  useEffect(() => {
    // Listen for service worker updates
    const handleServiceWorkerUpdate = (event) => {
      if (event.data) {
        switch (event.data.type) {
          case 'UPDATE_AVAILABLE':
            console.log('🔄 App update available');
            setShowUpdate(true);
            break;
          case 'UPDATE_READY':
            console.log('✅ App update ready');
            setShowUpdate(true);
            break;
          case 'CACHE_UPDATE':
            if (event.data.status === 'COMPLETED') {
              console.log('📦 Cache updated');
              // Don't show notification for cache updates, they happen in background
            }
            break;
          case 'UPDATE_PROGRESS':
            setDownloadProgress(event.data.progress || 0);
            setCurrentTask(event.data.task || '');
            setEstimatedTime(event.data.estimatedTime || null);
            break;
          case 'UPDATE_COMPLETE':
            setDownloadProgress(100);
            setCurrentTask('Update complete');
            setEstimatedTime(null);
            break;
          default:
            break;
        }
      }
    };

    // Register service worker message listener
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleServiceWorkerUpdate);
    }

    // Check for waiting service worker on load
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then((registration) => {
        if (registration.waiting) {
          console.log('🔄 Service worker update waiting');
          setShowUpdate(true);
        }
      });
    }

    return () => {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handleServiceWorkerUpdate);
      }
    };
  }, []);

  const handleRefresh = async () => {
    setIsUpdating(true);
    setDownloadProgress(0);
    setCurrentTask('Preparing update...');
    
    try {
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.ready;
        
        if (registration.waiting) {
          // Tell the waiting service worker to skip waiting
          setCurrentTask('Activating new version...');
          registration.waiting.postMessage({ type: 'SKIP_WAITING' });
          
          // Wait for the new service worker to take control
          const waitForControllerChange = new Promise((resolve) => {
            navigator.serviceWorker.addEventListener('controllerchange', resolve, { once: true });
          });
          
          await waitForControllerChange;
          setDownloadProgress(90);
        }
      }
      
      setCurrentTask('Finalizing...');
      setDownloadProgress(95);
      
      // Small delay to ensure service worker is ready
      setTimeout(() => {
        setDownloadProgress(100);
        setCurrentTask('Complete');
        window.location.reload();
      }, 100);
      
    } catch (error) {
      console.error('Error updating app:', error);
      // Fallback: just reload
      window.location.reload();
    }
  };

  const handleDismiss = () => {
    setShowUpdate(false);
  };

  if (!showUpdate) {
    return null;
  }

  return (
    <div className="update-notification">
      <div className="update-content">
        <div className="update-icon">
          <BiRefresh size={24} />
        </div>
        <div className="update-text">
          <div className="update-title">New Update Available</div>
          <div className="update-message">
            {isUpdating ? (
              <>
                <div className="update-progress-info">
                  <span className="update-task">{currentTask}</span>
                  {estimatedTime && (
                    <span className="update-time">
                      {Math.ceil(estimatedTime / 1000)}s remaining
                    </span>
                  )}
                </div>
                <div className="update-progress-bar">
                  <div 
                    className="update-progress-fill" 
                    style={{ width: `${downloadProgress}%` }}
                  />
                </div>
                <div className="update-progress-percent">
                  {Math.round(downloadProgress)}%
                </div>
              </>
            ) : (
              'A new version of the app is ready. Refresh to get the latest features and recipes.'
            )}
          </div>
        </div>
        <div className="update-actions">
          <button 
            className="update-btn update-btn-refresh" 
            onClick={handleRefresh}
            disabled={isUpdating}
          >
            {isUpdating ? (
              <>
                <BiRefresh className="spinning" size={16} />
                Updating...
              </>
            ) : (
              <>
                <BiRefresh size={16} />
                Refresh App
              </>
            )}
          </button>
          <button 
            className="update-btn update-btn-dismiss" 
            onClick={handleDismiss}
            disabled={isUpdating}
          >
            <BiX size={16} />
            Later
          </button>
        </div>
      </div>
    </div>
  );
};

export default UpdateNotification;